/**
 * VariantPickerSets - Custom element for product set variant selection
 * Handles product bundle creation, pricing, and cart operations
 */
class VariantPickerSets extends HTMLElement {

  static SELECTORS = {
    ITEM: '.variant-picker-sets__item',
    OPTIONS: '.variant-picker-sets__options',
    CHOICE: '.variant-picker-sets__choice',
    CHOICE_LABEL: '.variant-picker-sets__choice-label',
    CHECKBOX: '.sets-choice__checkbox',
    VALUE_BUTTON: '.variant-picker-sets__value',
    CTA_BUTTON: '.sets-sticky-cta__button',
    PRICE_BLOCK: '[data-static-price]',
    VARIANTS_SCRIPT: 'script[data-variants]',
    MEDIA_BUTTON: '[data-action="open-gallery"]',
    MEDIA_SCRIPT: 'script[data-media]',
    GALLERY_MODAL: '#sets-product-gallery-modal',
  };


  static ATTRIBUTES = {
    ACTION_TOGGLE: '[data-action="toggle-item"]',
    ACTION_ATC: '[data-action="sets-atc"]',
    PRODUCT_ID: 'productId',
    OPTION_POSITION: 'optionPosition',
    VALUE: 'value',
    SPECIAL_PRICE: 'specialPriceCents',
  };


  static CLASSES = {
    OPEN: 'is-open',
    SELECTED: 'is-selected',
    INCLUDED: 'is-included',
    LINE_THROUGH: 'line-through',
  };


  static DEFAULTS = {
    CURRENCY: 'USD',
    MONEY_FORMAT: '${{amount}}',
    MIN_SELECTIONS: 1,
    CENTS_DIVISOR: 100,
  };


  static LABELS = {
    ADD_TO_SET: ((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).addToSet,
    INCLUDED: ((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).included,
    SELECT_PRODUCTS: ((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).selectProducts,
    ADD_TO_CART: ((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).addToCart,
    ADD_SET: ((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).addSet,
  };

  // Set Group Configuration
  static SET_GROUP = {
    TAG_PREFIX: 'set_group:',
    SEPARATOR: ':',
  };

  // Required Product Tags
  // Also can be set in theme settings
  static REQUIRED_TAGS = {
    BRA: ['bra', 'Bra', 'swim_top', 'bikini_top'],
    PANTY: ['panty', 'panties', 'Panty', 'Panties', 'swim_bottom', 'bikini_bottom'],
    SWIMWEAR_TOP: ['swim_top', 'bikini_top'],
    SWIMWEAR_BOTTOM: ['swim_bottom', 'bikini_bottom'],
    GARTER_BELT: ['garter_belt', 'garter-belt', 'garter belt', 'garter', 'Garter'],
    LEG_GARTER: ['leg_garter', 'leg-garter', 'leg garter', 'thigh_garter'],
  };

  constructor() {
    super();
    // Bind once so removeEventListener can find the same reference across reconnects
    this._clickHandler = this.handleClick.bind(this);
    this.initializeProperties();
    this.cacheElements();
  }

  initializeProperties() {
    this.requiredCount = this.parseNumber(this.dataset.requiredCount);
    this.parentVariantId = this.dataset.parentVariantId;
    this.parentPriceCents = this.parseNumber(this.dataset.parentPriceCents);
    this.setGroup = this.dataset.setGroup || '';
    this.context = this.dataset.context || 'main_product';
    this.galleryModalId = this.dataset.galleryModalId || VariantPickerSets.SELECTORS.GALLERY_MODAL.replace('#', '');
    this.selectedItems = new Map();
    this.moneyFormat = window.themeVariables?.settings?.moneyFormat || VariantPickerSets.DEFAULTS.MONEY_FORMAT;
    this.requiredTags = this._resolveRequiredTags();
    this.extraPriceCents = this.parseNumber(this.dataset.extraPriceCents);
    this.braAndPantyPriceCents = this.parseNumber(this.dataset.braAndPantyPriceCents);
    this.legGarterPriceCents = this.parseNumber(this.dataset.legGarterPriceCents);
    this.giftWrapPriceCents = this.parseNumber(this.dataset.giftWrapPriceCents);
    this.parentComparePriceCents = this.parseNumber(this.dataset.parentCompareAtPriceCents);
    this.giftWrapEnabled = false;

    // Cart total in cents — initialized from the free-shipping-bar element (server-rendered),
    // then kept up to date via cart:change events for the free shipping label logic.
    this.cartTotalCents = parseInt(
      document.querySelector('custom-free-shipping-bar')?.getAttribute('total-price') || '0',
      10
    );
    this._onCartChange = (e) => {
      const cart = e.detail?.cart;
      if (!cart) return;
      const itemTotal = (cart.items || [])
        .filter(i => i.requires_shipping)
        .reduce((sum, i) => sum + i.final_line_price, 0);
      const discounts = (cart.cart_level_discount_applications || [])
        .reduce((sum, d) => sum + d.total_allocated_amount, 0);
      this.cartTotalCents = itemTotal - discounts;
    };
  }

  _resolveRequiredTags() {
    const parseTags = (str) => str ? str.split(',').map(t => t.trim()).filter(Boolean) : null;
    const braTags = parseTags(window.themeVariables?.settings?.setsBraTags);
    const pantyTags = parseTags(window.themeVariables?.settings?.setsPantyTags);
    const swimwearTopTags = parseTags(window.themeVariables?.settings?.setsSwimwearTopTags);
    const swimwearBottomTags = parseTags(window.themeVariables?.settings?.setsSwimwearBottomTags);
    // GARTER_BELT: reads from new setsGarterBeltTags, falls back to legacy setsGarterTags
    const garterBeltTags = parseTags(window.themeVariables?.settings?.setsGarterBeltTags)
      || parseTags(window.themeVariables?.settings?.setsGarterTags);
    const legGarterTags = parseTags(window.themeVariables?.settings?.setsLegGarterTags);
    return {
      BRA: braTags || VariantPickerSets.REQUIRED_TAGS.BRA,
      PANTY: pantyTags || VariantPickerSets.REQUIRED_TAGS.PANTY,
      SWIMWEAR_TOP: swimwearTopTags || VariantPickerSets.REQUIRED_TAGS.SWIMWEAR_TOP,
      SWIMWEAR_BOTTOM: swimwearBottomTags || VariantPickerSets.REQUIRED_TAGS.SWIMWEAR_BOTTOM,
      GARTER_BELT: garterBeltTags || VariantPickerSets.REQUIRED_TAGS.GARTER_BELT,
      LEG_GARTER: legGarterTags || VariantPickerSets.REQUIRED_TAGS.LEG_GARTER,
    };
  }

  cacheElements() {
    // Target ATC buttons by data-action so both old and new UI are covered
    this.ctaButtons = this.querySelectorAll('[data-action="sets-atc"]');
    this.items = null;
    // Legacy sticky/static CTA — unused in new UI
    this.stickyCta = null;
    this.staticCta = null;
    // Sticky bottom bar
    this.stickyBar = this.querySelector('[data-vps-stk]');
  }

  connectedCallback() {
    this.bindEvents();
    document.addEventListener('cart:change', this._onCartChange);
    this.initializeSetGroups();
    this.initializeStickyObserver();
    this.updateCta();
    this._teleportPfgPopup();
  }

  initializeStickyObserver() {
    if (!this.stickyBar) return;

    const productStickyBar = document.querySelector('.product-sticky-bar');

    // ── Desktop (≥ 1000px) ───────────────────────────────────────────────────
    // vps-stk is position:sticky in the right column.
    // product-sticky-bar is hidden while vps-stk is on screen, shown after.
    const desktopObserver = new IntersectionObserver(
      ([entry]) => {
        if (window.innerWidth < 1000) return;
        productStickyBar?.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0 }
    );

    desktopObserver.observe(this);

    // ── Mobile (< 1000px) ────────────────────────────────────────────────────
    // vps-stk (fixed bar) shows when the inline summary ATC button scrolls
    // out of view, and hides when it comes back into view.
    const mainBtn = this.querySelector('.ss-atc');
    if (mainBtn) {
      const mobileObserver = new IntersectionObserver(
        ([entry]) => {
          if (window.innerWidth >= 1000) return;
          this.stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
          this.stickyBar.toggleAttribute('aria-hidden', entry.isIntersecting);
        },
        { threshold: 0 }
      );
      mobileObserver.observe(mainBtn);
    }

    // ── Desktop positioning ──────────────────────────────────────────────────
    // Pin vps-stk inside the product-info column on desktop
    this._positionStickyBar();
    this._stickyResizeObserver = new ResizeObserver(() => this._positionStickyBar());
    this._stickyResizeObserver.observe(document.documentElement);
    window.addEventListener('resize', () => this._positionStickyBar(), { passive: true });
  }

  _positionStickyBar() {
    if (!this.stickyBar || window.innerWidth < 1000) return;
    const container = this.closest('.product-info') || this;
    const rect = container.getBoundingClientRect();
    this.stickyBar.style.left = `${rect.left}px`;
    this.stickyBar.style.width = `${rect.width}px`;
  }

  bindEvents() {
    // Remove before adding to prevent duplicate listeners when connectedCallback fires
    // multiple times (e.g. when Modal moves element to document.body via shouldAppendToBody)
    this.removeEventListener('click', this._clickHandler);
    this.addEventListener('click', this._clickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._clickHandler);
    document.removeEventListener('cart:change', this._onCartChange);
    if (this._pfgSheet && this._pfgSheet.parentNode === document.body) {
      document.body.removeChild(this._pfgSheet);
    }
    if (this._pfgOverlay && this._pfgOverlay.parentNode === document.body) {
      document.body.removeChild(this._pfgOverlay);
    }
  }

  _teleportPfgPopup() {
    const sheet = this.querySelector('[data-pfg-sheet]');
    const overlay = this.querySelector('[data-pfg-overlay]');

    if (sheet) {
      document.body.appendChild(sheet);
      this._pfgSheet = sheet;
      const closeBtn = sheet.querySelector('[data-action="close-pfg"]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.closePfgPopup());
    }

    if (overlay) {
      document.body.appendChild(overlay);
      this._pfgOverlay = overlay;
      overlay.addEventListener('click', () => this.closePfgPopup());
    }
  }


  parseNumber(value, fallback = 0) {
    const parsed = Number(value);
    return isNaN(parsed) ? fallback : parsed;
  }

  safeQuerySelector(element, selector) {
    try {
      return element?.querySelector(selector) || null;
    } catch {
      return null;
    }
  }

  getCurrency() {
    return Shopify?.currency?.active || window.Shopify?.currency || VariantPickerSets.DEFAULTS.CURRENCY;
  }

  formatMoney(cents) {
    try {
      const value = this.parseNumber(cents) / VariantPickerSets.DEFAULTS.CENTS_DIVISOR;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.getCurrency()
      }).format(value);
    } catch {
      return `${cents / VariantPickerSets.DEFAULTS.CENTS_DIVISOR}`;
    }
  }

  // Like formatMoney but drops trailing ".00" when there are no cents
  formatMoneyCompact(cents) {
    try {
      const value = this.parseNumber(cents) / VariantPickerSets.DEFAULTS.CENTS_DIVISOR;
      const hasCents = value % 1 !== 0;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.getCurrency(),
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: hasCents ? 2 : 0,
      }).format(value);
    } catch {
      return this.formatMoney(cents);
    }
  }

  // Replace {{key}} placeholders in a locale template string with values from vars map
  _statusText(template, vars = {}) {
    return Object.entries(vars).reduce(
      (str, [k, v]) => str.split(`{{${k}}}`).join(v),
      template
    );
  }

  // Set Group Management
  initializeSetGroups() {
    this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM).forEach(itemEl => {
      const itemSetGroup = this.getProductSetGroup(itemEl);
      itemEl.dataset.itemSetGroup = itemSetGroup || '';
    });
  }

  extractSetGroupFromTags(tags) {
    if (!Array.isArray(tags)) return null;

    const setGroupTag = tags.find(tag =>
      tag && tag.startsWith(VariantPickerSets.SET_GROUP.TAG_PREFIX)
    );

    if (!setGroupTag) return null;

    const parts = setGroupTag.split(VariantPickerSets.SET_GROUP.SEPARATOR);
    return parts.length > 1 ? parts[1].trim() : null;
  }

  extractAllSetGroupsFromTags(tags) {
    if (!Array.isArray(tags)) return [];

    return tags
      .filter(tag => tag && tag.startsWith(VariantPickerSets.SET_GROUP.TAG_PREFIX))
      .map(tag => {
        const parts = tag.split(VariantPickerSets.SET_GROUP.SEPARATOR);
        return parts.length > 1 ? parts[1].trim() : null;
      })
      .filter(Boolean);
  }

  getProductSetGroup(itemEl) {
    const groups = this.getProductSetGroups(itemEl);
    return groups.length > 0 ? groups[0] : null;
  }

  getProductSetGroups(itemEl) {
    if (!itemEl) return [];

    try {
      if (itemEl.dataset && itemEl.dataset.productTags) {
        const tags = itemEl.dataset.productTags.split(',').map(tag => tag.trim());
        return this.extractAllSetGroupsFromTags(tags);
      }

      const productData = this.getItemVariants(itemEl)?.[0];
      if (productData?.product?.tags) {
        return this.extractAllSetGroupsFromTags(productData.product.tags);
      }

      return [];
    } catch (error) {
      console.error('Set Group Debug - Error getting product set groups:', error);
      return [];
    }
  }

  getProductTags(itemEl) {
    if (!itemEl) return [];

    try {
      if (itemEl.dataset && itemEl.dataset.productTags) {
        return itemEl.dataset.productTags.split(',').map(tag => tag.trim());
      }

      const productData = this.getItemVariants(itemEl)?.[0];
      if (productData?.product?.tags) {
        return Array.isArray(productData.product.tags) ? productData.product.tags : [];
      }

      return [];
    } catch {
      return [];
    }
  }

  hasRequiredTag(itemEl, requiredTags) {
    const productTags = this.getProductTags(itemEl);
    return requiredTags.some(requiredTag =>
      productTags.some(tag => tag.toLowerCase() === requiredTag.toLowerCase())
    );
  }

  countProductsWithTag(requiredTags) {
    let count = 0;

    for (const [productId, selection] of this.selectedItems) {
      if (!selection.variant) continue;

      const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
      if (!itemEl) continue;

      if (this.hasRequiredTag(itemEl, requiredTags)) {
        count++;
      }
    }

    return count;
  }

  countAllProductsWithTag(requiredTags) {
    let count = 0;

    const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);
    for (const itemEl of allItems) {
      if (this.hasRequiredTag(itemEl, requiredTags)) {
        count++;
      }
    }

    return count;
  }

  validateSetGroupConsistency() {
    if (this.selectedItems.size <= 1) return { isValid: true, setGroup: null };

    // Collect set groups per product, then find intersection across all products.
    // A product may belong to multiple sets (multiple set_group: tags), so we need
    // to find a set group that ALL selected products share.
    // Leg garter items are always add-ons and are excluded from this check.
    const legGarterTagsList = this.requiredTags.LEG_GARTER.map(t => t.toLowerCase());
    let intersection = null;

    for (const [productId, selection] of this.selectedItems) {
      if (!selection.variant) continue;

      const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);

      if (!itemEl) {
        console.warn('Set Group Debug - Could not find item element for productId:', productId);
        continue;
      }

      // Leg garter is a standalone add-on — skip it when computing set group intersection
      const tags = (itemEl.dataset.productTags || '').split(',').map(t => t.trim().toLowerCase());
      if (legGarterTagsList.some(lt => tags.includes(lt))) continue;

      const productSetGroups = this.getProductSetGroups(itemEl);

      if (productSetGroups.length === 0) {
        // Product has no set_group tags — it can't belong to any set group
        intersection = [];
        break;
      }

      if (intersection === null) {
        intersection = productSetGroups;
      } else {
        intersection = intersection.filter(g => productSetGroups.includes(g));
      }

      if (intersection.length === 0) break;
    }

    const commonGroups = intersection || [];
    const isValid = commonGroups.length > 0;
    const setGroup = isValid ? commonGroups[0] : null;

    return { isValid, setGroup, groupCount: commonGroups.length };
  }

  isValidCompleteSet() {
    const { isValid, setGroup } = this.validateSetGroupConsistency();

    if (!isValid || !setGroup) return { isComplete: false, setGroup: null };

    let setGroupCount = 0;
    let hasBraProduct = false;
    let hasPantyProduct = false;

    for (const [productId, selection] of this.selectedItems) {
      if (!selection.variant) continue;

      const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
      if (!itemEl) continue;

      if (this.getProductSetGroups(itemEl).includes(setGroup)) {
        setGroupCount++;
        if (this.hasRequiredTag(itemEl, this.requiredTags.BRA)) hasBraProduct = true;
        if (this.hasRequiredTag(itemEl, this.requiredTags.PANTY)) hasPantyProduct = true;
      }
    }

    const { braAvailable, pantyAvailable } = this._getSetTypesAvailable(setGroup);
    const bothTypesRequired = braAvailable && pantyAvailable;
    const typeRequirementMet = !bothTypesRequired || (hasBraProduct && hasPantyProduct);

    // Set is complete only if we have required count AND type requirements are met.
    // For KISSBUNDLES sets, use the bundle component count as the effective minimum.
    const effectiveRequired = this._getEffectiveMin();
    const isComplete = effectiveRequired > 0 &&
                      setGroupCount >= effectiveRequired &&
                      typeRequirementMet;

    return { isComplete, setGroup, setGroupCount, hasBraProduct, hasPantyProduct, bothTypesRequired, typeRequirementMet };
  }

  // Returns true when this set page includes a garter belt product (making it a 3-item set page)
  _hasGarterBeltItem() {
    const garterBeltTagsList = this.requiredTags.GARTER_BELT.map(t => t.toLowerCase());
    const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);
    for (const itemEl of allItems) {
      const tags = (itemEl.dataset.productTags || '').split(',').map(t => t.trim().toLowerCase());
      if (garterBeltTagsList.some(gt => tags.includes(gt))) return true;
    }
    return false;
  }

  _getSetTypesAvailable(setGroup) {
    let braAvailable = false;
    let pantyAvailable = false;

    const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);
    for (const itemEl of allItems) {
      if (setGroup && !this.getProductSetGroups(itemEl).includes(setGroup)) continue;
      if (this.hasRequiredTag(itemEl, this.requiredTags.BRA)) braAvailable = true;
      if (this.hasRequiredTag(itemEl, this.requiredTags.PANTY)) pantyAvailable = true;
      if (braAvailable && pantyAvailable) break;
    }

    return { braAvailable, pantyAvailable };
  }

  _validateTypeCombination() {
    const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);
    let braAvailable = false;
    let pantyAvailable = false;

    for (const itemEl of allItems) {
      if (this.hasRequiredTag(itemEl, this.requiredTags.BRA)) braAvailable = true;
      if (this.hasRequiredTag(itemEl, this.requiredTags.PANTY)) pantyAvailable = true;
      if (braAvailable && pantyAvailable) break;
    }

    if (!braAvailable || !pantyAvailable) {
      return { required: false, hasBra: false, hasPanty: false, isValid: true };
    }

    let hasBra = false;
    let hasPanty = false;

    for (const [productId, selection] of this.selectedItems) {
      if (!selection.variant) continue;
      const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
      if (!itemEl) continue;
      if (this.hasRequiredTag(itemEl, this.requiredTags.BRA)) hasBra = true;
      if (this.hasRequiredTag(itemEl, this.requiredTags.PANTY)) hasPanty = true;
    }

    return { required: true, hasBra, hasPanty, isValid: hasBra && hasPanty };
  }


  handleClick(event) {
    if (this.handleGalleryClick(event)) return;
    if (this.handleActionClick(event)) return;
    if (this.handleToggleClick(event)) return;
    if (this.handleVariantClick(event)) return;
    if (this.handleAddToCartClick(event)) return;
  }

  handleToggleClick(event) {
    const toggleElement = event.target.closest(VariantPickerSets.ATTRIBUTES.ACTION_TOGGLE);
    if (!toggleElement) return false;
    if (!this.contains(toggleElement)) return false;

    const itemEl = event.target.closest(VariantPickerSets.SELECTORS.ITEM);
    if (!itemEl) return true;

    // New UI: "Add to set" button always opens the size picker
    this.setItemState(itemEl, 'selecting');
    return true;
  }

  handleVariantClick(event) {
    const valueButton = event.target.closest(VariantPickerSets.SELECTORS.VALUE_BUTTON);
    if (!valueButton) return false;
    if (!this.contains(valueButton)) return false;

    // Ignore clicks on disabled buttons
    if (valueButton.disabled || valueButton.dataset.available === 'false') {
      return true;
    }

    const itemEl = valueButton.closest(VariantPickerSets.SELECTORS.ITEM);
    if (!itemEl) return true;

    this.selectVariantOption(itemEl, valueButton);
    return true;
  }

  async handleAddToCartClick(event) {
    const btn = event.target.closest(VariantPickerSets.ATTRIBUTES.ACTION_ATC);
    if (!btn) return false;
    if (!this.contains(btn)) return false;

    // Nothing selected — scroll to picker and highlight incomplete items
    if (this.selectedItems.size === 0) {
      const header = document.querySelector('.shopify-section--header');
      const offset = (header ? header.offsetHeight : 0) + 16;
      const top = this.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
      this.highlightIncompleteItems();
      return true;
    }

    // Show loading state immediately for instant feedback
    this.setCtaLoading(true);
    await this.addSetToCart();
    this.closest('quick-buy-modal')?.hide();
    return true;
  }

  handleGalleryClick(event) {
    const mediaButton = event.target.closest(VariantPickerSets.SELECTORS.MEDIA_BUTTON);
    if (!mediaButton) return false;
    if (!this.contains(mediaButton)) return false;

    const itemEl = mediaButton.closest(VariantPickerSets.SELECTORS.ITEM);
    if (!itemEl) return true;

    this.openGalleryModal(itemEl);
    return true;
  }

  openGalleryModal(itemEl) {
    const mediaScript = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.MEDIA_SCRIPT);
    if (!mediaScript) return;

    try {
      const media = JSON.parse(mediaScript.textContent);
      const productTitle = itemEl.dataset.productTitle || '';
      const modal = document.getElementById(this.galleryModalId);

      if (!modal || !media || media.length === 0) return;

      // Filter only images
      const imageMedia = media.filter(item => item.media_type === 'image');
      if (imageMedia.length === 0) return;

      // Update modal title
      const titleEl = modal.querySelector('.sets-gallery-modal__title');
      if (titleEl) {
        titleEl.textContent = productTitle;
      }

      // Update full details link
      const productUrl = itemEl.dataset.productUrl;
      const linkEl = modal.querySelector('.sets-gallery-modal__link');
      if (linkEl && productUrl) {
        linkEl.href = productUrl;
      }

      // Render slides
      const slidesContainer = modal.querySelector('.sets-gallery-modal__slides');
      const thumbnailsContainer = modal.querySelector('.sets-gallery-modal__thumbnails');

      if (slidesContainer) {
        slidesContainer.innerHTML = '';
        imageMedia.forEach((item, index) => {
          const slide = document.createElement('div');
          slide.className = 'sets-gallery-modal__slide';
          slide.dataset.index = index;

          const img = document.createElement('img');
          img.src = item.preview_image.url;
          img.alt = item.alt || productTitle;
          img.loading = 'lazy';
          img.className = 'sets-gallery-modal__image';

          slide.appendChild(img);
          slidesContainer.appendChild(slide);
        });
      }

      if (thumbnailsContainer) {
        thumbnailsContainer.innerHTML = '';
        imageMedia.forEach((item, index) => {
          const thumbnail = document.createElement('button');
          thumbnail.type = 'button';
          thumbnail.className = 'sets-gallery-modal__thumbnail';
          thumbnail.dataset.index = index;
          thumbnail.setAttribute('aria-label', (((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).goToImage || '').replace('__INDEX__', index + 1));

          const img = document.createElement('img');
          img.src = item.preview_image.url;
          img.alt = item.alt || productTitle;
          img.loading = 'lazy';

          thumbnail.appendChild(img);
          thumbnail.addEventListener('click', () => this.goToSlide(index));
          thumbnailsContainer.appendChild(thumbnail);
        });
      }

      // Initialize slider
      this.currentSlideIndex = 0;
      this.totalSlides = imageMedia.length;

      // Update slider after a short delay to ensure DOM is ready
      setTimeout(() => {
        this.updateSlider();
      }, 0);

      // Add navigation handlers
      const prevButton = modal.querySelector('.sets-gallery-modal__prev');
      const nextButton = modal.querySelector('.sets-gallery-modal__next');

      if (prevButton) {
        prevButton.onclick = () => this.previousSlide();
      }
      if (nextButton) {
        nextButton.onclick = () => this.nextSlide();
      }

      // Add keyboard navigation
      this.setupKeyboardNavigation(modal);

      // Open modal
      modal.show();
    } catch (error) {
      console.error('Error opening gallery modal:', error);
    }
  }

  setupKeyboardNavigation(modal) {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previousSlide();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.nextSlide();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        modal.hide();
      }
    };

    // Remove existing listener if any
    if (this.galleryKeyDownHandler) {
      modal.removeEventListener('keydown', this.galleryKeyDownHandler);
    }

    // Add new listener
    this.galleryKeyDownHandler = handleKeyDown;
    modal.addEventListener('keydown', this.galleryKeyDownHandler);

    // Clean up on modal close
    const handleClose = () => {
      modal.removeEventListener('keydown', this.galleryKeyDownHandler);
      modal.removeEventListener('dialog:after-hide', handleClose);
      this.galleryKeyDownHandler = null;
    };
    modal.addEventListener('dialog:after-hide', handleClose);
  }

  goToSlide(index) {
    if (index < 0 || index >= this.totalSlides) return;
    this.currentSlideIndex = index;
    this.updateSlider();
  }

  nextSlide() {
    if (this.currentSlideIndex < this.totalSlides - 1) {
      this.currentSlideIndex++;
    } else {
      this.currentSlideIndex = 0;
    }
    this.updateSlider();
  }

  previousSlide() {
    if (this.currentSlideIndex > 0) {
      this.currentSlideIndex--;
    } else {
      this.currentSlideIndex = this.totalSlides - 1;
    }
    this.updateSlider();
  }

  updateSlider() {
    const modal = document.getElementById(this.galleryModalId);
    if (!modal) return;

    const slides = modal.querySelectorAll('.sets-gallery-modal__slide');
    const thumbnails = modal.querySelectorAll('.sets-gallery-modal__thumbnail');
    const prevButton = modal.querySelector('.sets-gallery-modal__prev');
    const nextButton = modal.querySelector('.sets-gallery-modal__next');

    // Update slides visibility
    slides.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === this.currentSlideIndex);
    });

    // Update thumbnails
    thumbnails.forEach((thumbnail, index) => {
      thumbnail.classList.toggle('is-active', index === this.currentSlideIndex);
    });

    // Update navigation buttons
    if (prevButton) {
      prevButton.disabled = this.totalSlides <= 1;
    }
    if (nextButton) {
      nextButton.disabled = this.totalSlides <= 1;
    }
  }


  isItemSelected(productId) {
    const selection = this.selectedItems.get(productId);
    return Boolean(selection?.variant);
  }

  isOptionsOpen(itemEl) {
    const options = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.OPTIONS);
    return options?.classList.contains(VariantPickerSets.CLASSES.OPEN) || false;
  }

  deselectItem(itemEl, productId) {
    this.clearSelectionForItem(itemEl);
    this.setChoiceState(itemEl, false);
    this.selectedItems.delete(productId);
    this.closeOptions(itemEl);
    this.updateCta();
  }

  openOptions(itemEl) {
    // Delegate to setItemState so new UI buttons are also updated
    this.setItemState(itemEl, 'selecting');
  }

  closeOptions(itemEl) {
    // Delegate to setItemState so new UI buttons are also updated
    const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
    const isSelected = this.isItemSelected(productId);
    this.setItemState(itemEl, isSelected ? 'confirmed' : 'add');
  }

  selectVariantOption(itemEl, valueButton) {
    const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
    const variants = this.getItemVariants(itemEl);

    if (!variants) return;

    this.updateOptionSelection(itemEl, valueButton);

    // Always update availability after selecting any option
    this.updateVariantAvailability(itemEl, variants);

    const variant = this.findMatchingVariant(itemEl, productId, variants);

    if (variant) {
      this.applyVariantSelection(itemEl, productId, variant);
    }

    this.updateCta();
  }

  updateOptionSelection(itemEl, selectedButton) {
    const optionFieldset = selectedButton.closest('fieldset');
    if (!optionFieldset) return;

    // Clear previous selections in this option group
    optionFieldset
      .querySelectorAll(VariantPickerSets.SELECTORS.VALUE_BUTTON)
      .forEach(btn => btn.classList.remove(VariantPickerSets.CLASSES.SELECTED));

    selectedButton.classList.add(VariantPickerSets.CLASSES.SELECTED);

    // Update legend to show selected value: "Band size: 34"
    const legend = optionFieldset.querySelector('.szl');
    if (legend) {
      const baseName = legend.dataset.baseName || legend.textContent.trim();
      if (!legend.dataset.baseName) legend.dataset.baseName = baseName;
      const selectedValue = selectedButton.dataset[VariantPickerSets.ATTRIBUTES.VALUE];
      legend.textContent = selectedValue ? `${baseName}: ${selectedValue}` : baseName;
    }
  }

  findMatchingVariant(itemEl, productId, variants) {
    const selection = this.selectedItems.get(productId) || { options: {}, variant: null };

    // Get all selected options for this product
    const optionFieldsets = itemEl.querySelectorAll('fieldset[data-option-position]');
    const totalOptions = optionFieldsets.length;
    let selectedCount = 0;

    optionFieldsets.forEach(fieldset => {
      const optionPosition = this.parseNumber(fieldset.dataset[VariantPickerSets.ATTRIBUTES.OPTION_POSITION]);
      const selectedOption = fieldset.querySelector(`.${VariantPickerSets.CLASSES.SELECTED}`);

      if (selectedOption) {
        const optionValue = selectedOption.dataset[VariantPickerSets.ATTRIBUTES.VALUE];
        selection.options[optionPosition] = optionValue;
        selectedCount++;
      }
    });

    // Only proceed if all options are selected
    if (selectedCount < totalOptions) {
      return null;
    }

    // Find matching variant that is available
    const matchingVariant = variants.find(variant =>
      Object.keys(selection.options).every(pos =>
        variant[`option${pos}`] === selection.options[pos]
      )
    );

    // Return only if variant is available
    return matchingVariant?.available ? matchingVariant : null;
  }

  applyVariantSelection(itemEl, productId, variant) {
    const selection = this.selectedItems.get(productId) || { options: {}, variant: null };
    const wasSelected = Boolean(selection?.variant);
    variant.product_id = productId; // Store DOM product ID in variant
    selection.variant = variant;
    selection.productId = productId; // Also store in selection
    this.selectedItems.set(productId, selection);

    this.setChoiceState(itemEl, true);
    this.updateItemPrice(itemEl, variant);

    // Delay visual transition so the user can see their completed selection
    if (itemEl._confirmTimeout) clearTimeout(itemEl._confirmTimeout);
    itemEl._confirmTimeout = setTimeout(() => {
      itemEl._confirmTimeout = null;
      this.setItemState(itemEl, 'confirmed', variant.title);
    }, 600);
  }

  getItemVariants(itemEl) {
    try {
      const scriptsElement = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.VARIANTS_SCRIPT);
      return scriptsElement ? JSON.parse(scriptsElement.textContent) : null;
    } catch {
      return null;
    }
  }

  /**
   * Update availability of option buttons based on current selections
   */
  updateVariantAvailability(itemEl, variants) {
    const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];

    // Get currently selected options from DOM
    const currentSelections = {};
    const optionFieldsets = itemEl.querySelectorAll('fieldset[data-option-position]');

    optionFieldsets.forEach(fieldset => {
      const optionPosition = this.parseNumber(fieldset.dataset[VariantPickerSets.ATTRIBUTES.OPTION_POSITION]);
      const selectedButton = fieldset.querySelector(`.${VariantPickerSets.CLASSES.SELECTED}`);

      if (selectedButton) {
        const optionValue = selectedButton.dataset[VariantPickerSets.ATTRIBUTES.VALUE];
        currentSelections[optionPosition] = optionValue;
      }
    });

    // Update availability for each option
    optionFieldsets.forEach(fieldset => {
      const optionPosition = this.parseNumber(fieldset.dataset[VariantPickerSets.ATTRIBUTES.OPTION_POSITION]);
      const buttons = fieldset.querySelectorAll(VariantPickerSets.SELECTORS.VALUE_BUTTON);

      buttons.forEach(button => {
        const value = button.dataset[VariantPickerSets.ATTRIBUTES.VALUE];

        // Check if there's any available variant with this value
        const isAvailable = variants.some(variant => {
          if (!variant.available) return false;
          if (variant[`option${optionPosition}`] !== value) return false;

          // Check if this variant matches all currently selected options (except this position)
          return Object.keys(currentSelections).every(pos => {
            const posNum = this.parseNumber(pos);
            if (posNum === optionPosition) return true; // Skip current position being evaluated
            return variant[`option${pos}`] === currentSelections[pos];
          });
        });

        button.disabled = !isAvailable;
        button.dataset.available = isAvailable;
      });
    });
  }

  // UI State Management
  clearSelectionForItem(itemEl) {
    itemEl
      .querySelectorAll(`.${VariantPickerSets.CLASSES.SELECTED}`)
      .forEach(btn => btn.classList.remove(VariantPickerSets.CLASSES.SELECTED));

    itemEl.querySelectorAll('.szl').forEach(legend => {
      if (legend.dataset.baseName) {
        legend.textContent = legend.dataset.baseName;
      }
    });
  }

  setChoiceState(itemEl, included) {
    const checkbox = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.CHECKBOX);
    const label = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.CHOICE_LABEL);
    const choiceRow = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.CHOICE);

    if (checkbox) checkbox.checked = included;
    if (label) label.textContent = included ? VariantPickerSets.LABELS.INCLUDED : VariantPickerSets.LABELS.ADD_TO_SET;
    if (choiceRow) choiceRow.classList.toggle(VariantPickerSets.CLASSES.INCLUDED, included);
  }

  updateItemPrice(itemEl, variant) {
    const priceBlock = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.PRICE_BLOCK);
    if (!priceBlock) return;

    const priceSpan = priceBlock.querySelector('span');
    const compareSpan = priceBlock.querySelector(`.${VariantPickerSets.CLASSES.LINE_THROUGH}`);

    if (priceSpan) priceSpan.textContent = this.formatMoney(variant.price);
    if (compareSpan && variant.compare_at_price) {
      compareSpan.textContent = this.formatMoney(variant.compare_at_price);
    }
  }

  animateDropdown(element, open) {
    if (!element) return;

    element.style.height = `${element.scrollHeight}px`;
    void element.offsetHeight; // Force reflow

    element.classList.toggle(VariantPickerSets.CLASSES.OPEN, open);
    element.style.height = open ? `${element.scrollHeight}px` : '0px';

    const cleanup = () => {
      if (open) element.style.height = 'auto';
      element.removeEventListener('transitionend', cleanup);
    };

    element.addEventListener('transitionend', cleanup);
  }

  // Data Access Methods
  getSelectedVariants() {
    return Array.from(this.selectedItems.values())
      .filter(selection => selection.variant)
      .map(selection => selection.variant);
  }

  getOrderedSelectedVariants() {
    if (!this.items) {
      this.items = Array.from(this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM));
    }

    return this.items.reduce((acc, itemEl) => {
      const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
      const selection = this.selectedItems.get(productId);

      if (selection?.variant) {
        acc.push(selection.variant);
      }
      return acc;
    }, []);
  }

  // Price Calculation Methods
  // useSpecialPrice: true only for extra items in a complete set (not for set base items or incomplete sets)
  calculateItemPrice(variant, itemEl, useSpecialPrice = false) {
    if (!itemEl) {
      return this.parseNumber(variant.price);
    }

    const variantPrice = this.parseNumber(variant.price);

    if (useSpecialPrice) {
      const specialPrice = this.parseNumber(itemEl.dataset[VariantPickerSets.ATTRIBUTES.SPECIAL_PRICE]);
      if (specialPrice > 0) return specialPrice;
    }

    return variantPrice;
  }

  getTotalPriceCents() {
    let total = 0;

    for (const [productId, selection] of this.selectedItems) {
      if (!selection.variant) continue;

      total += this.parseNumber(selection.variant.price);
    }

    return total;
  }

  getBundlePriceCents() {
    const ordered = this.getOrderedSelectedVariants();
    const count = Math.min(this.requiredCount, ordered.length);

    let total = 0;
    for (let i = 0; i < count; i++) {
      total += this.parseNumber(ordered[i].price);
    }

    return total;
  }

  calculateTotalPrice() {
    const { isValid, setGroup } = this.validateSetGroupConsistency();

    if (!isValid) {
      return this.getTotalPriceCents();
    }

    const { isComplete, hasBraProduct } = this.isValidCompleteSet();
    let totalCents = 0;

    if (isComplete && hasBraProduct && this.parentPriceCents > 0) {
      const garterBeltTagsList = this.requiredTags.GARTER_BELT.map(t => t.toLowerCase());
      const legGarterTagsList = this.requiredTags.LEG_GARTER.map(t => t.toLowerCase());
      const bottomTagsListLC = this.requiredTags.PANTY.map(t => t.toLowerCase());
      const setProducts = [];
      const extraProducts = [];

      for (const [productId, selection] of this.selectedItems) {
        if (!selection.variant) continue;

        const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
        if (!itemEl) {
          extraProducts.push(selection.variant);
          continue;
        }

        const tags = (itemEl.dataset.productTags || '').split(',').map(t => t.trim().toLowerCase());
        // Leg garter is always a separate add-on, never bundled into the set
        if (legGarterTagsList.some(lt => tags.includes(lt))) {
          extraProducts.push(selection.variant);
          continue;
        }

        if (this.getProductSetGroups(itemEl).includes(setGroup)) {
          setProducts.push(selection.variant);
        } else {
          extraProducts.push(selection.variant);
        }
      }

      // Dynamic bundle size: 3 when garter belt is selected, 2 otherwise
      const hasGarterBeltInBundle = setProducts.some(variant => {
        const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${variant.product_id}"]`);
        const tags = (itemEl?.dataset.productTags || '').split(',').map(t => t.trim().toLowerCase());
        return garterBeltTagsList.some(gt => tags.includes(gt));
      });
      const effectiveBundleCount = hasGarterBeltInBundle ? 3 : 2;

      // Base price:
      //  - garter belt in bundle → product.price (the full 3-piece set price)
      //  - bra+panty only on a 3-item set page → braAndPantyPriceCents (intermediate tier)
      //  - bra+panty on a 2-item set page → product.price (the set price)
      const isThreeItemSetPage = this._hasGarterBeltItem();
      if (hasGarterBeltInBundle) {
        totalCents = this.parentPriceCents;
      } else if (isThreeItemSetPage && this.braAndPantyPriceCents > 0) {
        totalCents = this.braAndPantyPriceCents;
      } else {
        totalCents = this.parentPriceCents;
      }

      // Use selectBundleProducts to match cart logic (bra + panty priority)
      const bundleProducts = this.selectBundleProducts(setProducts, effectiveBundleCount);
      const bundleSet = new Set(bundleProducts);
      const remainderSetProducts = setProducts.filter(v => !bundleSet.has(v));

      // Only the first extra panty gets the special discounted price
      let extraPantySpecialApplied = false;
      [...extraProducts, ...remainderSetProducts].forEach(variant => {
        const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${variant.product_id}"]`);
        const tags = (itemEl?.dataset.productTags || '').split(',').map(t => t.trim().toLowerCase());
        const isLegGarter = legGarterTagsList.some(lt => tags.includes(lt));
        const isExtraPanty = bottomTagsListLC.some(bt => tags.includes(bt));
        if (isLegGarter && this.legGarterPriceCents > 0) {
          totalCents += this.legGarterPriceCents;
        } else if (isExtraPanty && !extraPantySpecialApplied) {
          totalCents += this.calculateItemPrice(variant, itemEl, true);
          extraPantySpecialApplied = true;
        } else {
          totalCents += this.calculateItemPrice(variant, itemEl, false);
        }
      });
    } else {
      totalCents = this.getTotalPriceCents();
    }

    return Math.max(0, totalCents);
  }

  // CTA Management — delegates to updateSummary for new UI
  updateCta() {
    this.updateSummary();
    this.dispatchEvent(new CustomEvent('set:selection-changed', { bubbles: true }));
  }

  updateSetGroupStatus(setGroup, isValid, isComplete, setGroupCount) {
    this.classList.toggle('has-set-group', Boolean(setGroup));
    this.classList.toggle('has-valid-set-group', isValid);
    this.classList.toggle('has-complete-set', isComplete);

    this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM).forEach(itemEl => {
      if (itemEl) {
        const itemSetGroups = this.getProductSetGroups(itemEl);
        const itemSetGroup = itemSetGroups[0] || '';
        itemEl.toggleAttribute('data-in-set-group', Boolean(setGroup) && itemSetGroups.includes(setGroup));
        itemEl.dataset.itemSetGroup = itemSetGroup;
      }
    });

    // let statusEl = this.querySelector('.set-group-status');
    // if (!statusEl) {
    //   statusEl = document.createElement('div');
    //   statusEl.className = 'set-group-status';
    //   const firstChild = this.querySelector('.v-stack');
    //   if (firstChild) {
    //     firstChild.appendChild(statusEl);
    //   }
    // }

    // // Only show status if minimal set is collected
    // if (setGroup && isValid) {
    //   // Count products with the current setGroup
    //   let currentSetGroupCount = 0;
    //   for (const [productId, selection] of this.selectedItems) {
    //     if (!selection.variant) continue;
    //     const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
    //     if (itemEl && this.getProductSetGroup(itemEl) === setGroup) {
    //       currentSetGroupCount++;
    //     }
    //   }

    //   const braCount = this.countProductsWithTag(VariantPickerSets.REQUIRED_TAGS.BRA);
    //   const totalPantyCount = this.countAllProductsWithTag(VariantPickerSets.REQUIRED_TAGS.PANTY);
    //   const hasMinimalSet = currentSetGroupCount >= this.requiredCount && braCount > 0;

    //   if (hasMinimalSet && totalPantyCount >= 2) {
    //     statusEl.textContent = 'Claim extra panties at $25, no code needed.';
    //     statusEl.className = 'set-group-status set-group-status--complete';
    //   } else {
    //     statusEl.textContent = '';
    //     statusEl.className = 'set-group-status set-group-status--hidden';
    //   }
    // } else {
    //   statusEl.textContent = '';
    //   statusEl.className = 'set-group-status set-group-status--hidden';
    // }
  }

  // Loading state for CTA buttons
  setCtaLoading(isLoading) {
    this.ctaButtons.forEach(btn => {
      if (isLoading) {
        btn.setAttribute('aria-busy', 'true');
        btn.style.position = 'relative';
        btn.style.pointerEvents = 'none';
      } else {
        btn.removeAttribute('aria-busy');
        btn.style.position = '';
        btn.style.pointerEvents = '';
      }
    });
  }

  // Cart Operations
  async addSetToCart() {
    const selectedVariants = this.getSelectedVariants();
    if (selectedVariants.length === 0) {
      this.setCtaLoading(false);
      return;
    }

    try {
      const items = this.prepareCartItems(selectedVariants);
      await this.submitToCart(items);
      await this.handleCartSuccess();
    } catch (error) {
      console.error('Error adding set to cart:', error);
      this.handleCartError(error);
    } finally {
      this.setCtaLoading(false);
    }
  }

   /**
   * Returns the effective minimum selection count for the current set.
   * For KISSBUNDLES sets: smallest valid bundle component count.
   * For legacy sets: requiredCount metafield or MIN_SELECTIONS.
   * Falls back to legacy if all bundles have no components (broken app data).
   */
  _getEffectiveMin() {
    if (window.KISSBUNDLES?.bundles?.length) {
      const min = Math.min(...window.KISSBUNDLES.bundles.map(b => {
        const count = b.components ? Object.keys(b.components).filter(k => k).length : 0;
        return count > 0 ? count : Infinity;
      }));
      if (isFinite(min)) return min;
      // All bundles have broken/empty components → fall back to legacy
    }
    return this.requiredCount > 0 ? this.requiredCount : VariantPickerSets.DEFAULTS.MIN_SELECTIONS;
  }

  selectBundleProducts(setProducts, count) {
    if (setProducts.length <= count) {
      return setProducts.slice();
    }

    const bras = [];
    const panties = [];

    setProducts.forEach(variant => {
      const itemEl = this.querySelector(
        `${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${variant.product_id}"]`
      );
      if (this.hasRequiredTag(itemEl, this.requiredTags.BRA)) {
        bras.push(variant);
      } else if (this.hasRequiredTag(itemEl, this.requiredTags.PANTY)) {
        panties.push(variant);
      }
    });

    // If both types present — anchor bundle with one bra + one panty first
    if (bras.length > 0 && panties.length > 0) {
      const selected = [bras[0], panties[0]];

      if (selected.length >= count) {
        return selected.slice(0, count);
      }

      // Fill remaining slots in original selection order
      const selectedSet = new Set(selected);
      for (const variant of setProducts) {
        if (!selectedSet.has(variant) && selected.length < count) {
          selected.push(variant);
          selectedSet.add(variant);
        }
      }

      return selected;
    }

    // Fallback: original selection order
    return setProducts.slice(0, count);
  }

  prepareCartItems(selectedVariants) {
    // === NEW BUNDLE LOGIC ===
    if (window.KISSBUNDLES?.bundles?.length) {
      const picker      = new KissBundlePicker(window.KISSBUNDLES, window.KISS_VMAPS);
      const splitOption = window.KISSBUNDLES.split_option || null;

      // Phase 1: collect handles + raw component option values (before any mapping)
      const collectedItems = [];
      const splitValues = []; // per-component split values (must all match)

      for (const [productId, selection] of this.selectedItems) {
        if (!selection.variant) continue;
        const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
        if (!itemEl) continue;

        const handle = itemEl.dataset.productHandle;
        const rawOptions = {}; // component option name → value (unmapped)

        itemEl.querySelectorAll('fieldset[data-option-position]').forEach(fs => {
          const pos      = fs.dataset.optionPosition;
          const legendEl = fs.querySelector('legend');
          const name     = legendEl?.dataset.baseName || legendEl?.textContent?.trim();
          const value    = selection.variant[`option${pos}`];
          if (!name || !value) return;
          if (splitOption && name === splitOption) {
            splitValues.push(value);
          }
          rawOptions[name] = value;
        });

        collectedItems.push({ handle, variant: selection.variant, rawOptions });
      }

      // If split_option is set, all components must have the same value.
      // Mixed colors (bra=yellow, panty=black) → no bundle → fallback to legacy.
      let splitValue = null;
      if (splitOption) {
        const uniqueSplitValues = [...new Set(splitValues)];
        if (uniqueSplitValues.length !== 1) {
          console.warn('[KissBundles] Mixed split values — falling back to legacy:', uniqueSplitValues);
          // fall through to legacy
        } else {
          splitValue = uniqueSplitValues[0];
        }
      }

      // Phase 2: find bundle by handles (option_map not needed here)
      const allHandles = collectedItems.map(i => i.handle);
      const bundle = splitOption && splitValues.length > 0 && new Set(splitValues).size !== 1
        ? null
        : picker.findBundle(allHandles, splitValue);

      if (bundle) {
        // Phase 3: separate bundle items from extras
        const bundleComponentHandles = Object.values(bundle.components);
        const bundleItems = collectedItems.filter(i => bundleComponentHandles.includes(i.handle));
        const extraItems  = collectedItems.filter(i => !bundleComponentHandles.includes(i.handle));

        // Phase 4: build selectedOptions using per-component option maps.
        // bundle.component_option_maps: { [handle]: { compOptionName: bundleOptionName } }
        // Falls back to flat bundle.option_map, then global option_map, then name as-is.
        // Per-component maps are required when two components share the same option name
        // (e.g. bra "Size" → "Elara Size", panty "Size" → "Olivia Size").
        const componentOptionMaps = bundle.component_option_maps || {};
        const globalFlatMap = bundle.option_map || window.KISSBUNDLES.option_map || {};
        const selectedOptions = {};
        bundleItems.forEach(item => {
          const componentMap = componentOptionMaps[item.handle] || globalFlatMap;
          for (const [compName, value] of Object.entries(item.rawOptions)) {
            // If the component option name is already a bundle-level name, keep it as-is
            const bundleName = (bundle.option_order || []).includes(compName)
              ? compName
              : (componentMap[compName] || compName);
            selectedOptions[bundleName] = value;
          }
        });

        const variant = picker.findVariant(bundle, selectedOptions);

        if (variant && variant.available) {
          const items = [{ id: variant.id, quantity: 1, properties: { _set_url: window.location.pathname } }];
          extraItems.forEach(i => items.push({ id: i.variant.id, quantity: 1 }));
          return items;
        }

        if (variant && !variant.available) {
          console.warn('[KissBundles] Out of stock:', variant.id);
          return [];
        }

        console.error('[KissBundles] Variant not found — falling back to legacy:', { selectedOptions, option_order: bundle.option_order });
      } else {
        console.warn('[KissBundles] Bundle not found — falling back to legacy:', { allHandles, splitValue });
      }
    }
    // === END BUNDLE LOGIC — falls through to legacy if bundle/variant not found ===

    const { isValid, setGroup } = this.validateSetGroupConsistency();
    const { isComplete, hasBraProduct } = this.isValidCompleteSet();

    if (!isComplete || !isValid || !hasBraProduct || !this.parentVariantId) {
      return selectedVariants.map(variant => ({ id: variant.id, quantity: 1 }));
    }

    const items = [];
    const bundleId = `${setGroup || this.setGroup || 'set'}-${Date.now()}`;

    const garterBeltTagsList = this.requiredTags.GARTER_BELT.map(t => t.toLowerCase());
    const legGarterTagsList = this.requiredTags.LEG_GARTER.map(t => t.toLowerCase());
    const setProducts = [];
    const extraProducts = [];

    for (const [productId, selection] of this.selectedItems) {
      if (!selection.variant) continue;

      const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${productId}"]`);
      if (!itemEl) {
        extraProducts.push(selection.variant);
        continue;
      }

      const tags = (itemEl.dataset.productTags || '').split(',').map(t => t.trim().toLowerCase());
      // Leg garter is always a separate add-on, never bundled into the set
      if (legGarterTagsList.some(lt => tags.includes(lt))) {
        extraProducts.push(selection.variant);
        continue;
      }

      if (this.getProductSetGroups(itemEl).includes(setGroup)) {
        setProducts.push(selection.variant);
      } else {
        extraProducts.push(selection.variant);
      }
    }

    // Dynamic bundle size: 3 when garter belt is selected, 2 otherwise
    const hasGarterBeltInBundle = setProducts.some(variant => {
      const itemEl = this.querySelector(`${VariantPickerSets.SELECTORS.ITEM}[data-product-id="${variant.product_id}"]`);
      const tags = (itemEl?.dataset.productTags || '').split(',').map(t => t.trim().toLowerCase());
      return garterBeltTagsList.some(gt => tags.includes(gt));
    });
    const effectiveBundleCount = hasGarterBeltInBundle ? 3 : 2;

    const bundleProducts = this.selectBundleProducts(setProducts, effectiveBundleCount);
    const bundleSet = new Set(bundleProducts);
    const remainderSetProducts = setProducts.filter(v => !bundleSet.has(v));

    [...extraProducts, ...remainderSetProducts].forEach(variant => {
      items.push({ id: variant.id, quantity: 1 });
    });

    const labels = bundleProducts.map(variant => variant.name || variant.title);
    const parentProps = {
      _set_group: setGroup || this.setGroup,
      _set_role: 'parent',
      _set_bundle_id: bundleId,
      _set_type: setGroup ? `set_group:${setGroup}` : 'custom_set'
    };

    labels.forEach((label, index) => {
      parentProps[`Product ${index + 1}`] = label;
    });

    if (this.giftWrapEnabled) {
      parentProps['_luxury_gift_wrapping'] = 'true';
    }

    items.push({ id: this.parentVariantId, quantity: 1, properties: parentProps });

    return items;
  }

  async submitToCart(items) {
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Submit to Cart Debug - Error response:', errorText);
      throw new Error(`Cart submission failed: ${response.status} - ${errorText}`);
    }

    return response;
  }

  async handleCartSuccess() {
    await this.refreshCart();
  }

  handleCartError(error) {
    // Could implement user notification here
    console.error('Cart error:', error);
  }

  resetSelections() {
    this.selectedItems.clear();

    this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM).forEach(item => {
      this.setChoiceState(item, false);
      this.clearSelectionForItem(item);
    });

    this.updateCta();
  }

  highlightIncompleteItems() {
    const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);

    for (const itemEl of allItems) {
      const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
      if (this.isItemSelected(productId)) continue;

      // Open options if not already open
      const wasOpen = this.isOptionsOpen(itemEl);
      if (!wasOpen) {
        this.openOptions(itemEl);
      }

      // Scroll to the item
      this.scrollToItem(itemEl);

      // Wait for open animation before flashing
      const delay = wasOpen ? 0 : 350;
      setTimeout(() => {
        const fieldsets = itemEl.querySelectorAll('fieldset[data-option-position]');
        let flashedAny = false;

        fieldsets.forEach(fieldset => {
          const hasSelection = fieldset.querySelector(`.${VariantPickerSets.CLASSES.SELECTED}`);
          if (!hasSelection) {
            this.flashElement(fieldset);
            flashedAny = true;
          }
        });

        // Fallback: flash the choice row if no fieldsets visible yet
        if (!flashedAny) {
          const choice = itemEl.querySelector(VariantPickerSets.SELECTORS.CHOICE);
          if (choice) this.flashElement(choice);
        }
      }, delay);

      return; // Only handle first incomplete item
    }
  }

  highlightMissingTypeItems(missingBra, missingPanty) {
    const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);
    let firstHandled = false;

    for (const itemEl of allItems) {
      const isMissingType =
        (missingBra && this.hasRequiredTag(itemEl, this.requiredTags.BRA)) ||
        (missingPanty && this.hasRequiredTag(itemEl, this.requiredTags.PANTY));

      if (!isMissingType) continue;

      const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
      if (this.isItemSelected(productId)) continue;

      const wasOpen = this.isOptionsOpen(itemEl);
      if (!wasOpen) this.openOptions(itemEl);

      if (!firstHandled) {
        this.scrollToItem(itemEl);
        firstHandled = true;
      }

      const delay = wasOpen ? 0 : 350;
      setTimeout(() => {
        const fieldsets = itemEl.querySelectorAll('fieldset[data-option-position]');
        let flashedAny = false;

        fieldsets.forEach(fieldset => {
          const hasSelection = fieldset.querySelector(`.${VariantPickerSets.CLASSES.SELECTED}`);
          if (!hasSelection) {
            this.flashElement(fieldset);
            flashedAny = true;
          }
        });

        if (!flashedAny) {
          const choice = itemEl.querySelector(VariantPickerSets.SELECTORS.CHOICE);
          if (choice) this.flashElement(choice);
        }
      }, delay);
    }
  }

  scrollToItem(itemEl) {
    const header = document.querySelector('.shopify-section--header');
    const offset = (header ? header.offsetHeight : 0) + 40;
    const elementPosition = itemEl.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  }

  flashElement(element) {
    const triggerAnimation = (el, cls) => {
      el.classList.remove(cls);
      void el.offsetWidth; // force reflow to restart animation
      el.classList.add(cls);
      el.addEventListener('animationend', () => el.classList.remove(cls), { once: true });
    };

    const buttons = element.querySelectorAll(VariantPickerSets.SELECTORS.VALUE_BUTTON);
    if (buttons.length > 0) {
      // Shake the values row
      const valuesRow = element.querySelector('.variant-picker-sets__values');
      if (valuesRow) triggerAnimation(valuesRow, 'sets-shake-error');

      // Flash each available button red
      buttons.forEach(btn => {
        if (!btn.disabled) triggerAnimation(btn, 'sets-flash-error');
      });
    } else {
      // Fallback: shake the element itself
      triggerAnimation(element, 'sets-shake-error');
    }
  }

  scrollToFirstProduct() {
    const firstItem = this.querySelector(VariantPickerSets.SELECTORS.ITEM);
    if (!firstItem) return;

    const offset = document.querySelector('.shopify-section--header').offsetHeight + 40;
    const elementPosition = firstItem.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  // ─── New UI Methods ─────────────────────────────────────────────────────────

  /**
   * Handle clicks on data-action buttons not covered by other handlers.
   * Returns true if event was handled.
   */
  handleActionClick(event) {
    const btn = event.target.closest('[data-action]');
    if (!btn || !this.contains(btn)) return false;

    const action = btn.dataset.action;

    switch (action) {
      case 'collapse-item': {
        const itemEl = btn.closest(VariantPickerSets.SELECTORS.ITEM);
        if (itemEl) this.closeOptions(itemEl);
        return true;
      }
      case 'change-size': {
        const itemEl = btn.closest(VariantPickerSets.SELECTORS.ITEM);
        if (itemEl) this.setItemState(itemEl, 'selecting');
        return true;
      }
      case 'remove-item': {
        const itemEl = btn.closest(VariantPickerSets.SELECTORS.ITEM);
        if (itemEl) {
          const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
          this.clearSelectionForItem(itemEl);
          this.selectedItems.delete(productId);
          this.setItemState(itemEl, 'add');
          this.updateSummary();
        }
        return true;
      }
      case 'open-pfg':
        this.openPfgPopup();
        return true;
      case 'close-pfg':
        this.closePfgPopup();
        return true;
      case 'toggle-gift-wrap':
        this.toggleGiftWrap();
        return true;
      case 'toggle-gift-short': {
        const giftShort = this.querySelector('[data-gift-short]');
        if (!giftShort) return true;
        const isOpen = giftShort.classList.toggle('open');
        const header = giftShort.querySelector('.gift-short-header');
        if (header) header.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        return true;
      }
      case 'gift-know-size':
        this.activateGiftMode();
        return true;
      case 'gift-dont-know':
        this.openPfgPopup();
        this.activateGiftMode();
        return true;
      case 'scroll-to-item': {
        const targetId = btn.dataset.scrollTo;
        if (targetId) {
          const targetEl = document.getElementById(targetId);
          if (targetEl) {
            const header = document.querySelector('.shopify-section--header');
            const offset = (header ? header.offsetHeight : 0) + 16;
            const top = targetEl.getBoundingClientRect().top + window.pageYOffset - offset;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        }
        return true;
      }
      default:
        return false;
    }
  }

  activateGiftMode() {
    const giftShort = this.querySelector('[data-gift-short]');
    if (giftShort) {
      giftShort.classList.remove('open');
      const header = giftShort.querySelector('.gift-short-header');
      if (header) header.setAttribute('aria-expanded', 'false');
    }

    // Enable gift wrap
    this.giftWrapEnabled = true;
    const gwCb = this.querySelector('[data-gift-wrap-cb]');
    if (gwCb) gwCb.classList.add('checked');

    // Open all items in 'add' state so user can select sizes
    const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);
    allItems.forEach(itemEl => {
      const addRow = itemEl.querySelector('.tog-row--add');
      if (addRow && addRow.style.display !== 'none') {
        this.setItemState(itemEl, 'selecting');
      }
    });
  }

  updateGiftShortState() {
    const giftShort = this.querySelector('[data-gift-short]');
    if (!giftShort) return;

    const header = giftShort.querySelector('.gift-short-header');
    const choices = giftShort.querySelector('.gift-choices');
    const done = giftShort.querySelector('[data-gift-short-done]');

    let hasBra = false;
    let hasPanty = false;

    this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM).forEach(itemEl => {
      const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
      const selection = this.selectedItems.get(productId);
      if (!selection?.variant) return;
      if (this.hasRequiredTag(itemEl, this.requiredTags.BRA)) hasBra = true;
      if (this.hasRequiredTag(itemEl, this.requiredTags.PANTY)) hasPanty = true;
    });

    if (hasBra && hasPanty) {
      if (header) header.style.display = 'none';
      if (choices) choices.style.display = 'none';
      giftShort.classList.remove('open');
      if (done) {
        done.style.display = '';
        done.innerHTML = ((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).giftWrapNotice;
      }
    } else {
      if (header) header.style.display = '';
      if (done) done.style.display = 'none';
    }
  }

  /**
   * Set the visual state of a product item card.
   * @param {Element} itemEl
   * @param {'add'|'selecting'|'confirmed'} state
   * @param {string} [sizeLabel] - displayed size string when state === 'confirmed'
   */
  setItemState(itemEl, state, sizeLabel) {
    const addRow = itemEl.querySelector('.tog-row--add');
    const selRow = itemEl.querySelector('.tog-row--sel');
    const confirmedRow = itemEl.querySelector('.tog-confirmed');
    const options = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.OPTIONS);

    switch (state) {
      case 'add':
        itemEl.classList.remove('sel');
        if (addRow) addRow.style.display = '';
        if (selRow) selRow.style.display = 'none';
        if (confirmedRow) confirmedRow.style.display = 'none';
        if (options) {
          this.animateDropdown(options, false);
          options.classList.remove(VariantPickerSets.CLASSES.OPEN);
        }
        break;

      case 'selecting':
        itemEl.classList.add('sel');
        if (addRow) addRow.style.display = 'none';
        if (selRow) selRow.style.display = '';
        if (confirmedRow) confirmedRow.style.display = 'none';
        if (options) {
          this.animateDropdown(options, true);
          options.classList.add(VariantPickerSets.CLASSES.OPEN);
        }
        // Refresh availability when opening picker
        {
          const variants = this.getItemVariants(itemEl);
          if (variants) this.updateVariantAvailability(itemEl, variants);
        }
        break;

      case 'confirmed':
        itemEl.classList.add('sel');
        if (addRow) addRow.style.display = 'none';
        if (selRow) selRow.style.display = 'none';
        if (confirmedRow) {
          confirmedRow.style.display = '';
          if (sizeLabel) {
            const sizeEl = confirmedRow.querySelector('.bld-conf-size');
            if (sizeEl) sizeEl.textContent = sizeLabel;
          }
        }
        if (options) {
          this.animateDropdown(options, false);
          options.classList.remove(VariantPickerSets.CLASSES.OPEN);
        }
        break;
    }
  }

  /**
   * Update the summary section (.ss) at the bottom of the builder.
   * Shows when ≥1 item is selected; updates thumbnails, price, savings, and button text.
   */
  updateSummary() {
    const summaryEl = this.querySelector('[data-summary]');
    if (!summaryEl) return;

    if (this.selectedItems.size === 0) {
      summaryEl.style.display = 'none';
      this.updateGiftShortState();
      this.updateItemStatuses();
      this.updateLadder();
      this.updateStickyBar();
      return;
    }

    summaryEl.style.display = '';

    this.updateSummaryThumbnails();

    // Price & savings
    const totalCents = this.calculateTotalPrice();
    const totalEl = summaryEl.querySelector('[data-summary-total]');
    if (totalEl) totalEl.textContent = this.formatMoney(totalCents);

    // Savings = sum of compare_at prices minus actual total
    let compareTotal = 0;
    for (const [, selection] of this.selectedItems) {
      if (!selection.variant) continue;
      const cap = selection.variant.compare_at_price;
      compareTotal += (cap && cap > selection.variant.price) ? cap : selection.variant.price;
    }
    if (this.giftWrapEnabled && this.giftWrapPriceCents > 0) {
      compareTotal += this.giftWrapPriceCents;
    }
    const savings = compareTotal - totalCents;
    const savingsEl = summaryEl.querySelector('[data-summary-savings]');
    if (savingsEl) {
      if (savings > 0) {
        savingsEl.textContent = (((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).saveAmount || '').replace('__AMOUNT__', this.formatMoneyCompact(savings));
        savingsEl.classList.remove('hidden');
      } else {
        savingsEl.textContent = '';
        savingsEl.classList.add('hidden');
      }
    }

    // ATC button text — "Add set to cart" only when selected products form a complete set
    const atcBtn = summaryEl.querySelector('[data-action="sets-atc"]');
    if (atcBtn) {
      const { isComplete } = this.isValidCompleteSet();
      const label = isComplete ? VariantPickerSets.LABELS.ADD_SET : VariantPickerSets.LABELS.ADD_TO_CART;
      atcBtn.textContent = `${label} • ${this.formatMoney(totalCents)}`;
    }

    // Also refresh ctaButtons reference in case DOM changed
    this.ctaButtons = this.querySelectorAll('[data-action="sets-atc"]');

    this.updateGiftShortState();
    this.updateItemStatuses();
    this.updateLadder();
    this.updateStickyBar();
  }

  /**
   * Update the ladder progress bar in the summary section.
   * Shown only on sets with exactly 3 panties AND extra_item_price set.
   * States: step 1 (bra+1 panty), step 2 (bra+2 panties), step 3 (bra+3 panties).
   * Hidden when: no bra selected, or only panties selected.
   */
  updateLadder() {
    const ladderEl = this.querySelector('[data-ladder]');
    if (!ladderEl) return;

    const bottomTagsList = this.requiredTags.PANTY.map(t => t.toLowerCase());
    const topTagsList = this.requiredTags.BRA.map(t => t.toLowerCase());

    let hasBra = false;
    let confirmedPantyCount = 0;

    for (const [productId, selection] of this.selectedItems) {
      if (!selection?.variant) continue;
      const itemEl = this.querySelector(`[data-product-id="${productId}"]`);
      if (!itemEl) continue;
      const tags = (itemEl.dataset.productTags || '').split(',').map(t => t.trim().toLowerCase());
      if (topTagsList.some(tt => tags.includes(tt))) hasBra = true;
      if (bottomTagsList.some(bt => tags.includes(bt))) confirmedPantyCount++;
    }

    // Show only when bra is selected + at least 1 panty is selected
    if (!hasBra || confirmedPantyCount === 0) {
      ladderEl.style.display = 'none';
      return;
    }

    // Animate in on first reveal
    const wasHidden = ladderEl.style.display === 'none' || ladderEl.style.display === '';
    ladderEl.style.display = 'block';
    if (wasHidden) {
      ladderEl.style.animation = 'none';
      void ladderEl.offsetWidth;
      ladderEl.style.animation = '';
    }

    const currentStep = Math.min(confirmedPantyCount, 3);

    // Dot states: done / current / next (one step ahead gets pulse invitation)
    for (let i = 1; i <= 3; i++) {
      const stepEl = ladderEl.querySelector(`[data-ladder-step="${i}"]`);
      if (!stepEl) continue;
      stepEl.classList.remove('done', 'current', 'next', 'active');
      if (i < currentStep) stepEl.classList.add('done');
      else if (i === currentStep) stepEl.classList.add('current');
      else if (i === currentStep + 1) stepEl.classList.add('next');
    }

    // Hero step (step 2) — active class + badge text
    const heroStepEl = ladderEl.querySelector('[data-ladder-step="2"]');
    const heroBadgeEl = ladderEl.querySelector('[data-ladder-hero-badge]');
    if (heroStepEl && heroBadgeEl) {
      if (currentStep >= 2) {
        heroStepEl.classList.add('active');
        heroBadgeEl.textContent = '✓ ' + ((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).bestValue;
      } else {
        heroBadgeEl.textContent = ((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).bestValue;
      }
    }

    // Fill bar — step 1 → 35%, step 2 → 70%, step 3 → 100%
    const fillEl = ladderEl.querySelector('[data-ladder-fill]');
    if (fillEl) {
      const pct = currentStep === 1 ? 35 : currentStep === 2 ? 70 : 100;
      fillEl.style.width = (pct * 0.6666).toFixed(2) + '%';
    }

    // Price calculations
    // Get highest compare_at price and regular price across all panty items on the page
    let maxPantyCompareCents = 0;
    let maxPantyRegularCents = 0;
    this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM).forEach(itemEl => {
      const tags = (itemEl.dataset.productTags || '').split(',').map(t => t.trim().toLowerCase());
      if (!bottomTagsList.some(bt => tags.includes(bt))) return;
      const priceEl = itemEl.querySelector('[data-static-price]');
      if (!priceEl) return;
      const cap = parseInt(priceEl.dataset.comparePriceCents || '0', 10);
      const reg = parseInt(priceEl.dataset.origPriceCents || '0', 10);
      if (cap > maxPantyCompareCents) maxPantyCompareCents = cap;
      if (reg > maxPantyRegularCents) maxPantyRegularCents = reg;
    });

    const step1Price   = Math.round(this.parentPriceCents / 100) * 100;
    const step1Compare = Math.round(this.parentComparePriceCents / 100 || 0) * 100;
    const extra        = this.extraPriceCents;

    // step 2: first extra panty at special price
    const step2Price   = Math.round((step1Price + extra) / 100) * 100;
    const step2Compare = Math.round((step1Compare + maxPantyCompareCents) / 100) * 100;

    // step 3: second extra panty at its regular (default) price
    const step3ExtraPrice = maxPantyRegularCents || extra;
    const step3Price   = Math.round((step2Price + step3ExtraPrice) / 100) * 100;
    const step3Compare = Math.round((step2Compare + maxPantyCompareCents) / 100) * 100;

    const renderPrice = (priceEl, price, compare) => {
      if (!priceEl) return;
      priceEl.innerHTML = compare > price
        ? `${this.formatMoneyCompact(price)} <s>${this.formatMoneyCompact(compare)}</s>`
        : this.formatMoneyCompact(price);
    };

    renderPrice(ladderEl.querySelector('[data-ladder-price="1"]'), step1Price,   step1Compare);
    renderPrice(ladderEl.querySelector('[data-ladder-price="2"]'), step2Price,   step2Compare);
    renderPrice(ladderEl.querySelector('[data-ladder-price="3"]'), step3Price,   step3Compare);

    // Update anchor save amount dynamically
    const anchorSaveEl = ladderEl.querySelector('[data-ladder-anchor-save]');
    if (anchorSaveEl) {
      const step3Save = step3Compare - step3Price;
      anchorSaveEl.textContent = step3Save > 0 ? (((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).saveAmount || '').replace('__AMOUNT__', this.formatMoneyCompact(step3Save)) : '';
    }

    // Free shipping badges — show only for the step(s) that would actually unlock free shipping
    const ladderFreeShipThreshold = window.themeVariables?.settings?.freeShippingThresholdCents || 0;
    if (ladderFreeShipThreshold > 0) {
      const alreadyReached = this.cartTotalCents >= ladderFreeShipThreshold;
      const stepPrices = { 2: step2Price, 3: step3Price };
      for (const [step, price] of Object.entries(stepPrices)) {
        const shipEl = ladderEl.querySelector(`[data-ladder-ship="${step}"]`);
        if (!shipEl) continue;
        const unlocks = !alreadyReached && (this.cartTotalCents + price) >= ladderFreeShipThreshold;
        shipEl.style.display = unlocks ? '' : 'none';
      }
    } else {
      ladderEl.querySelectorAll('[data-ladder-ship]').forEach(el => { el.style.display = 'none'; });
    }

    // Nudge messages from theme settings
    const strings = window.themeVariables?.strings?.setStatus || {};
    const nudgeEl = ladderEl.querySelector('[data-ladder-nudge]');
    if (!nudgeEl) return;

    nudgeEl.classList.remove('ok');
    nudgeEl.classList.toggle('tappable', currentStep < 3);
    nudgeEl.onclick = currentStep < 3
      ? () => this.scrollIntoView({ behavior: 'smooth', block: 'start' })
      : null;

    if (currentStep === 1) {
      const tmpl = strings.ladderStep1 || 'Add a panty for just {{price}} — unlock free shipping';
      nudgeEl.innerHTML = tmpl.split('{{price}}').join(`<strong>${this.formatMoneyCompact(extra)}</strong>`);
    } else if (currentStep === 2) {
      const tmpl = strings.ladderStep2 || 'Best value unlocked — <strong>add one more to save even more</strong>';
      nudgeEl.innerHTML = tmpl.split('{{price}}').join(`<strong>${this.formatMoneyCompact(step3ExtraPrice)}</strong>`);
    } else {
      nudgeEl.classList.add('ok');
      const ladderStep3Tmpl = strings.ladderStep3 || 'Full set unlocked — maximum savings!';
      nudgeEl.innerHTML = ladderStep3Tmpl.split('{{price}}').join(`<strong>${this.formatMoneyCompact(step3Price)}</strong>`);
    }
  }

  /**
   * Sync the sticky bottom bar's thumbnails, savings, and button state
   * to mirror the main summary section.
   */
  updateStickyBar() {
    // Sync the product-sticky-bar proxy button (outside this element)
    const proxyBtn = document.querySelector('[data-vps-proxy-btn]');
    if (proxyBtn) {
      const hasSelection = this.selectedItems.size > 0;
      proxyBtn.ariaDisabled = hasSelection ? 'false' : 'true';
      proxyBtn.textContent = hasSelection
        ? (proxyBtn.dataset.addText    || VariantPickerSets.LABELS.ADD_TO_CART)
        : (proxyBtn.dataset.selectText || VariantPickerSets.LABELS.SELECT_PRODUCTS);
    }

    if (!this.stickyBar) return;

    // Desktop: visible by default, hidden once the user has selected at least one item
    this.stickyBar.classList.toggle('vps-stk--has-selection', this.selectedItems.size > 0);

    const btn = this.stickyBar.querySelector('[data-vps-stk-btn]');
    const savingsEl = this.stickyBar.querySelector('[data-vps-stk-sav]');
    const thumbsEl = this.stickyBar.querySelector('[data-vps-stk-thumbs]');
    const labelEl = this.stickyBar.querySelector('[data-vps-stk-btn-label]');
    const priceEl = this.stickyBar.querySelector('[data-vps-stk-btn-price]');

    if (this.selectedItems.size === 0) {
      if (btn) btn.ariaDisabled = 'true';
      if (labelEl) labelEl.textContent = VariantPickerSets.LABELS.SELECT_PRODUCTS;
      if (priceEl) priceEl.textContent = '';
      if (savingsEl) savingsEl.textContent = '';
      if (thumbsEl) thumbsEl.innerHTML = '';
      return;
    }

    // Thumbnails
    if (thumbsEl) {
      thumbsEl.innerHTML = '';
      const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);
      allItems.forEach(itemEl => {
        const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
        const selection = this.selectedItems.get(productId);
        if (!selection?.variant) return;

        let imgUrl = null;
        const mediaScript = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.MEDIA_SCRIPT);
        if (mediaScript) {
          try {
            const media = JSON.parse(mediaScript.textContent);
            const firstImage = media.find(m => m.media_type === 'image');
            if (firstImage) imgUrl = firstImage.preview_image.url;
          } catch {}
        }

        if (imgUrl) {
          const img = document.createElement('img');
          img.className = 'vps-stk__thumb';
          img.src = imgUrl;
          img.alt = itemEl.dataset.productTitle || '';
          img.width = 28;
          img.height = 28;
          img.loading = 'lazy';
          thumbsEl.appendChild(img);
        }
      });

      if (this.giftWrapEnabled) {
        const template = document.getElementById(`GiftWrapStickyTemplate-${this.dataset.productId}`);
        if (template && template.content) {
          thumbsEl.appendChild(template.content.cloneNode(true));
        }
      }
    }

    // Price & savings
    const totalCents = this.calculateTotalPrice();
    let compareTotal = 0;
    for (const [, selection] of this.selectedItems) {
      if (!selection.variant) continue;
      const cap = selection.variant.compare_at_price;
      compareTotal += (cap && cap > selection.variant.price) ? cap : selection.variant.price;
    }
    if (this.giftWrapEnabled && this.giftWrapPriceCents > 0) {
      compareTotal += this.giftWrapPriceCents;
    }
    const savings = compareTotal - totalCents;
    if (savingsEl) {
      savingsEl.textContent = savings > 0 ? (((window.themeVariables && window.themeVariables.strings && window.themeVariables.strings.sets) || {}).saveAmount || '').replace('__AMOUNT__', this.formatMoneyCompact(savings)) : '';
      savingsEl.classList.toggle('hidden', savings <= 0);
    }

    // Button
    if (btn) {
      btn.ariaDisabled = 'false';
      const { isComplete } = this.isValidCompleteSet();
      const label = isComplete ? VariantPickerSets.LABELS.ADD_SET : VariantPickerSets.LABELS.ADD_TO_CART;
      if (labelEl) labelEl.textContent = label;
      if (priceEl) priceEl.textContent = this.formatMoneyCompact(totalCents);
    }
  }

  /**
   * Update per-item price display and status text.
   *
   * Classification (by DOM order):
   *   topItemId       — first item with a bra/top tag
   *   setPantyId      — first confirmed panty (by confirmation order)
   *   garterBeltItemId — first item with a garter belt tag (3rd set piece, unlocks $119 tier)
   *   leg garter items — items with leg_garter tag (add-on, shown at special price)
   *   extra panties    — confirmed panties beyond the first
   *
   * Tier logic:
   *   2-piece set (top + panty)                    → setPriceCents ($89)
   *   3-piece full set (top + panty + garter belt) → fullSetPriceCents ($119)
   */
  updateItemStatuses() {
    const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);
    const topTagsList = this.requiredTags.BRA.map(t => t.toLowerCase());
    const bottomTagsList = this.requiredTags.PANTY.map(t => t.toLowerCase());
    const swimwearTopTagsList = this.requiredTags.SWIMWEAR_TOP.map(t => t.toLowerCase());
    const swimwearBottomTagsList = this.requiredTags.SWIMWEAR_BOTTOM.map(t => t.toLowerCase());
    const garterBeltTagsList = this.requiredTags.GARTER_BELT.map(t => t.toLowerCase());
    const legGarterTagsList = this.requiredTags.LEG_GARTER.map(t => t.toLowerCase());

    // ─── Pass 1: identify top item, first panty tags, and garter belt item ───
    let topItemId = null;
    let topItemTags = [];
    let firstPantyItemTags = [];
    let garterBeltItemId = null;

    for (const itemEl of allItems) {
      const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
      const tags = (itemEl.dataset.productTags || '').split(',').map(t => t.trim().toLowerCase());
      const isTop = topTagsList.some(tt => tags.includes(tt));
      const isBottom = bottomTagsList.some(bt => tags.includes(bt));
      const isGarterBelt = garterBeltTagsList.some(gt => tags.includes(gt));

      if (isTop && !topItemId) { topItemId = productId; topItemTags = tags; }
      if (isBottom && !firstPantyItemTags.length) { firstPantyItemTags = tags; }
      if (isGarterBelt && !garterBeltItemId) { garterBeltItemId = productId; }
    }

    // Confirmed panty IDs in the ORDER the user added them (selectedItems Map preserves insertion order)
    const confirmedPantyIds = [];
    for (const [productId, selection] of this.selectedItems) {
      if (!selection?.variant) continue;
      const itemEl = this.querySelector(`[data-product-id="${productId}"]`);
      if (!itemEl) continue;
      const tags = (itemEl.dataset.productTags || '').split(',').map(t => t.trim().toLowerCase());
      if (bottomTagsList.some(bt => tags.includes(bt))) confirmedPantyIds.push(productId);
    }

    // The "set panty" is whichever panty the user confirmed FIRST
    const setPantyId = confirmedPantyIds[0] || null;
    // Number of extra panties (beyond the set panty); max 1 gets the special price
    const extraPantyCount = Math.max(0, confirmedPantyIds.length - 1);
    const extraSlotTaken = extraPantyCount >= 1;
    const extraItemText = this.dataset.extraZeroPriceText || '';
    const topConfirmed = Boolean(this.selectedItems.get(topItemId)?.variant);
    const bottomConfirmed = confirmedPantyIds.length > 0;
    const garterBeltConfirmed = Boolean(this.selectedItems.get(garterBeltItemId)?.variant);
    const setComplete = topConfirmed && bottomConfirmed;
    const fullSetComplete = topConfirmed && bottomConfirmed && garterBeltConfirmed;

    // Labels
    const topIsSwimwear = swimwearTopTagsList.some(t => topItemTags.includes(t));
    const topLabel = topIsSwimwear ? 'top' : 'bra';

    const refPantyIsSwimwear = swimwearBottomTagsList.some(t => firstPantyItemTags.includes(t));
    const genericBottomLabel = refPantyIsSwimwear ? 'bottom' : 'panty';

    const extraPriceCents = this.extraPriceCents || 0;
    // setPriceCents: bra+panty intermediate price on 3-item set pages; falls back to product.price
    const setPriceCents = this.braAndPantyPriceCents || this.parentPriceCents;
    // fullSetPriceCents: full 3-piece set price = product.price (parentPriceCents)
    const fullSetPriceCents = this.parentPriceCents;
    // legGarterPriceCents: add-on price for leg garter; falls back to extraPriceCents
    const legGarterPriceCents = this.legGarterPriceCents || extraPriceCents;

    const strings = window.themeVariables?.strings?.setStatus || {};

    // Free shipping threshold logic (for extra panties and garter belt)
    const pendingSetTotalCents = this.calculateTotalPrice();
    const freeShippingThresholdCents = window.themeVariables?.settings?.freeShippingThresholdCents || 0;
    const effectiveTotalCents = this.cartTotalCents + pendingSetTotalCents;
    const freeShippingAlreadyReached = freeShippingThresholdCents > 0 && effectiveTotalCents >= freeShippingThresholdCents;
    const extraUnlocksFreeShipping = freeShippingThresholdCents > 0
      && !freeShippingAlreadyReached
      && (effectiveTotalCents + extraPriceCents) >= freeShippingThresholdCents;
    // Adding garter belt switches total from setPriceCents to fullSetPriceCents
    const garterBeltUnlocksFreeShipping = freeShippingThresholdCents > 0
      && !freeShippingAlreadyReached
      && (this.cartTotalCents + fullSetPriceCents) >= freeShippingThresholdCents;

    // ─── Pass 2: update each item ─────────────────────────────────────────────
    for (const itemEl of allItems) {
      const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
      const isConfirmed = Boolean(this.selectedItems.get(productId)?.variant);
      const tags = (itemEl.dataset.productTags || '').split(',').map(t => t.trim().toLowerCase());
      const isTopItem = productId === topItemId;
      const isPantyItem = bottomTagsList.some(bt => tags.includes(bt));
      const isGarterBeltItem = productId === garterBeltItemId;
      const isLegGarterItem = !isTopItem && !isPantyItem && !isGarterBeltItem
        && legGarterTagsList.some(lt => tags.includes(lt));

      const priceRow = itemEl.querySelector('[data-static-price]');
      const statusEl = itemEl.querySelector('[data-item-status]');

      // ─── Price display ────────────────────────────────────────────────────
      // Extra panties and leg garter show a special sale price when the 2-piece set is complete.
      // Garter belt is a set tier piece — its price is reflected in the tier display, not here.
      if ((isPantyItem || isLegGarterItem) && !isTopItem && priceRow) {
        const origCents = this.parseNumber(priceRow.dataset.origPriceCents);
        const compareCents = this.parseNumber(priceRow.dataset.comparePriceCents);
        const saleSpan = priceRow.querySelector('.si-sale, .si-reg');
        const origSpan = priceRow.querySelector('.si-orig');

        let showSpecialPrice = false;
        let specialPriceCents = 0;

        if (isPantyItem && setComplete && extraPriceCents > 0 && productId === confirmedPantyIds[1]) {
          showSpecialPrice = true;
          specialPriceCents = extraPriceCents;
        } else if (isLegGarterItem && setComplete && legGarterPriceCents > 0) {
          showSpecialPrice = true;
          specialPriceCents = legGarterPriceCents;
        }

        if (showSpecialPrice && saleSpan) {
          const wasCents = compareCents > origCents ? compareCents : origCents;
          saleSpan.textContent = this.formatMoney(specialPriceCents);
          saleSpan.classList.add('si-sale');
          saleSpan.classList.remove('si-reg');
          if (origSpan) {
            origSpan.textContent = this.formatMoney(wasCents);
            origSpan.style.display = '';
          } else if (wasCents > 0) {
            const dynOrig = document.createElement('span');
            dynOrig.className = 'si-orig igPrice js-spt-dyn-orig';
            dynOrig.textContent = this.formatMoney(wasCents);
            saleSpan.insertAdjacentElement('afterend', dynOrig);
          }
        } else if (saleSpan) {
          priceRow.querySelectorAll('.js-spt-dyn-orig').forEach(el => el.remove());
          if (origCents > 0) {
            if (compareCents > origCents) {
              saleSpan.textContent = this.formatMoney(origCents);
              saleSpan.classList.add('si-sale');
              saleSpan.classList.remove('si-reg');
              if (origSpan) {
                origSpan.textContent = this.formatMoney(compareCents);
                origSpan.style.display = '';
              }
            } else {
              saleSpan.textContent = this.formatMoney(origCents);
              saleSpan.classList.add('si-reg');
              saleSpan.classList.remove('si-sale');
              if (origSpan) origSpan.style.display = 'none';
            }
          }
        }
      }

      // ─── Status text ──────────────────────────────────────────────────────
      if (!statusEl) continue;

      if (isTopItem) {
        // ── Bra / swim top ──
        if (fullSetComplete && this.requiredCount === 3) {
          // Bra + panty + garter belt all confirmed (3-piece sets only)
          statusEl.textContent = this._statusText(strings.fullSetPricingActive || '✓ Full set pricing active', { top: topLabel, bottom: genericBottomLabel, price: this.formatMoney(fullSetPriceCents) });
          statusEl.style.color = '#2D7A4F';
        } else if (setComplete) {
          // Bra + panty confirmed, no garter belt yet
          statusEl.textContent = this._statusText(strings.setPricingActive || '✓ Set pricing active', { top: topLabel, bottom: genericBottomLabel, price: this.formatMoney(setPriceCents) });
          statusEl.style.color = '#2D7A4F';
        } else if (topConfirmed && !bottomConfirmed) {
          // Bra confirmed but no panty yet
          statusEl.textContent = this._statusText(strings.addBottomToComplete || 'Add a {{bottom}} to complete your set', { bottom: genericBottomLabel });
          statusEl.style.color = '#8C8C8C';
        } else if (!topConfirmed && garterBeltConfirmed && this.requiredCount === 3) {
          // Garter belt selected but no bra → push toward full set (3-piece sets only)
          statusEl.textContent = this._statusText(strings.addBraUnlockFullSet || 'Add bra → unlock {{price}} full set', { price: this.formatMoney(fullSetPriceCents) });
          statusEl.style.color = '#BC0304';
        } else if (!topConfirmed && bottomConfirmed) {
          // Panty confirmed but no bra yet
          statusEl.textContent = this._statusText(strings.addTopUnlockSet || 'Add {{top}} → unlock {{price}} set', { top: topLabel, price: this.formatMoney(setPriceCents) });
          statusEl.style.color = '#BC0304';
        } else {
          statusEl.textContent = '';
        }

      } else if (isPantyItem) {
        // ── Panty / swim bottom ──
        const itemIsSwimwear = swimwearBottomTagsList.some(t => tags.includes(t));
        const itemLabel = itemIsSwimwear ? 'bottom' : 'panty';

        // Update add button text (show extra price when set complete and this is an extra panty)
        const addBtn = itemEl.querySelector('.tog-row--add .tog-add');
        if (addBtn) {
          const baseText = addBtn.dataset.baseText || addBtn.textContent.trim();
          if (!addBtn.dataset.baseText) addBtn.dataset.baseText = baseText;
          if (!isConfirmed && setComplete && productId !== setPantyId) {
            if (!extraSlotTaken && extraPriceCents > 0) {
              addBtn.textContent = `${baseText} — ${this.formatMoney(extraPriceCents)}`;
            } else if (extraSlotTaken) {
              addBtn.textContent = extraItemText || baseText;
            } else {
              addBtn.textContent = baseText;
            }
          } else {
            addBtn.textContent = baseText;
          }
        }

        if (garterBeltConfirmed && !topConfirmed && this.requiredCount === 3) {
          // Garter belt selected but no bra → all items push toward full set (3-piece sets only)
          statusEl.textContent = this._statusText(strings.addBraUnlockFullSet || 'Add bra → unlock {{price}} full set', { price: this.formatMoney(fullSetPriceCents) });
          statusEl.style.color = '#BC0304';
        } else if (isConfirmed && productId === setPantyId) {
          if (fullSetComplete && this.requiredCount === 3) {
            statusEl.textContent = this._statusText(strings.fullSetPricingActive || '✓ Full set pricing active', { top: topLabel, bottom: genericBottomLabel, price: this.formatMoney(fullSetPriceCents) });
            statusEl.style.color = '#2D7A4F';
          } else if (topConfirmed) {
            // Set panty + bra confirmed
            statusEl.textContent = this._statusText(strings.setPriceConfirmed || '✓ Set pricing active', { top: topLabel, bottom: genericBottomLabel, price: this.formatMoney(setPriceCents) });
            statusEl.style.color = '#2D7A4F';
          } else {
            // Panty confirmed but no bra yet
            statusEl.textContent = this._statusText(strings.addTopForSet || 'Add {{top}} → set for {{price}}', { top: topLabel, price: this.formatMoney(setPriceCents) });
            statusEl.style.color = '#BC0304';
          }
        } else if (isConfirmed && productId === confirmedPantyIds[1] && topConfirmed) {
          // First extra panty confirmed with bra active — special price applied
          statusEl.textContent = this._statusText(strings.extraConfirmed || '✓ Special price applied', { label: itemLabel, price: this.formatMoney(extraPriceCents) });
          statusEl.style.color = '#2D7A4F';
        } else if (isConfirmed && extraSlotTaken) {
          // 3rd+ panty confirmed (no special price), or extra panty confirmed with no bra
          const slotFullText = strings.extraSlotFull || '';
          statusEl.textContent = slotFullText ? this._statusText(slotFullText, { label: itemLabel }) : '';
          statusEl.style.color = '#2d7a4f';
        } else if (!isConfirmed && topConfirmed && !bottomConfirmed) {
          // Bra confirmed, no panty yet → show on ALL panty cards
          statusEl.textContent = this._statusText(strings.addBottomForSet || 'Add {{label}} → unlock {{price}} set', { label: itemLabel, price: this.formatMoney(setPriceCents) });
          statusEl.style.color = '#BC0304';
        } else if (!isConfirmed && topConfirmed && bottomConfirmed && !extraSlotTaken) {
          // 2-piece set active, extra slot still open — show add-extra upsell
          let tmpl;
          const isExtraPriceEmpty = this.dataset.extraPriceCents === undefined || this.dataset.extraPriceCents.trim() === "";
          if (isExtraPriceEmpty && this.dataset.extraZeroPriceText) {
            tmpl = this.dataset.extraZeroPriceText;
          } else {
            tmpl = extraUnlocksFreeShipping
              ? (strings.addExtraFreeShipping || 'Add extra {{label}} for {{price}} + get free shipping')
              : (strings.addExtra || 'Add extra {{label}} for {{price}}');
          }
          statusEl.textContent = this._statusText(tmpl, { label: itemLabel, price: this.formatMoney(extraPriceCents) });
          statusEl.style.color = '#BC0304';
        } else if (!isConfirmed && !topConfirmed && bottomConfirmed && !extraSlotTaken) {
          // One panty confirmed, no bra yet
          statusEl.textContent = this._statusText(strings.addTopForSet || 'Add {{top}} → set for {{price}}', { top: topLabel, price: this.formatMoney(setPriceCents) });
          statusEl.style.color = '#BC0304';
        } else {
          statusEl.textContent = '';
        }

      } else if (isGarterBeltItem) {
        // ── Garter belt — 3rd set piece that unlocks the $119 full set ──

        // Reset add button (no extra price on garter belt; it shifts the set tier)
        const addBtn = itemEl.querySelector('.tog-row--add .tog-add');
        if (addBtn) {
          const baseText = addBtn.dataset.baseText || addBtn.textContent.trim();
          if (!addBtn.dataset.baseText) addBtn.dataset.baseText = baseText;
          addBtn.textContent = baseText;
        }

        if (fullSetComplete) {
          // All 3 confirmed
          statusEl.textContent = this._statusText(strings.fullSetPricingActive || '✓ Full set pricing active', { top: topLabel, bottom: genericBottomLabel, price: this.formatMoney(fullSetPriceCents) });
          statusEl.style.color = '#2D7A4F';
        } else if (isConfirmed && !topConfirmed) {
          // Garter belt confirmed but no bra → need bra to unlock any set
          statusEl.textContent = this._statusText(strings.addBraUnlockFullSet || 'Add bra → unlock {{price}} full set', { price: this.formatMoney(fullSetPriceCents) });
          statusEl.style.color = '#BC0304';
        } else if (!isConfirmed && setComplete && this.requiredCount === 3) {
          // 2-piece set active — adding garter belt upgrades to full set (3-piece sets only)
          const tmpl = garterBeltUnlocksFreeShipping
            ? (strings.addGarterBeltUnlockFullSetFreeShipping || 'Add garter belt → unlock {{price}} full set + get free shipping')
            : (strings.addGarterBeltUnlockFullSet || 'Add garter belt → unlock {{price}} full set');
          statusEl.textContent = this._statusText(tmpl, { price: this.formatMoney(fullSetPriceCents) });
          statusEl.style.color = '#BC0304';
        } else {
          statusEl.textContent = '';
        }

      } else if (isLegGarterItem) {
        // ── Leg garter — add-on shown at special price when 2-piece set is active ──

        // Show special price on the add button when set is complete
        const addBtn = itemEl.querySelector('.tog-row--add .tog-add');
        if (addBtn) {
          const baseText = addBtn.dataset.baseText || addBtn.textContent.trim();
          if (!addBtn.dataset.baseText) addBtn.dataset.baseText = baseText;
          if (!isConfirmed && setComplete && legGarterPriceCents > 0) {
            addBtn.textContent = `${baseText} — ${this.formatMoney(legGarterPriceCents)}`;
          } else {
            addBtn.textContent = baseText;
          }
        }

        if (isConfirmed) {
          statusEl.textContent = this._statusText(strings.extraConfirmed || '✓ Special price applied', { label: 'leg garter', price: this.formatMoney(legGarterPriceCents) });
          statusEl.style.color = '#2D7A4F';
        } else if (setComplete && legGarterPriceCents > 0 && this.requiredCount === 3) {
          statusEl.textContent = this._statusText(strings.addLegGarterDetail || 'Add a little detail for {{price}}', { price: this.formatMoney(legGarterPriceCents) });
          statusEl.style.color = '#BC0304';
        } else {
          statusEl.textContent = '';
        }

      } else {
        // Non-classified item — clear status
        statusEl.textContent = '';
      }
    }
  }

  /**
   * Render thumbnail images of selected items into [data-summary-items].
   */
  updateSummaryThumbnails() {
    const container = this.querySelector('[data-summary-items]');
    if (!container) return;

    container.innerHTML = '';

    const allItems = this.querySelectorAll(VariantPickerSets.SELECTORS.ITEM);
    allItems.forEach(itemEl => {
      const productId = itemEl.dataset[VariantPickerSets.ATTRIBUTES.PRODUCT_ID];
      const selection = this.selectedItems.get(productId);
      if (!selection?.variant) return;

      let imgUrl = null;
      const mediaScript = this.safeQuerySelector(itemEl, VariantPickerSets.SELECTORS.MEDIA_SCRIPT);
      if (mediaScript) {
        try {
          const media = JSON.parse(mediaScript.textContent);
          const firstImage = media.find(m => m.media_type === 'image');
          if (firstImage) imgUrl = firstImage.preview_image.url;
        } catch {}
      }

      if (imgUrl) {
        const img = document.createElement('img');
        img.className = 'ss-th';
        img.src = imgUrl;
        img.alt = itemEl.dataset.productTitle || '';
        img.width = 44;
        img.height = 44;
        img.loading = 'lazy';
        container.appendChild(img);
      }
    });

    // Show one grey placeholder with '+' if there are more items to add
    if (this.selectedItems.size < allItems.length) {
      const nextPlaceholder = document.createElement('div');
      nextPlaceholder.className = 'ss-em ss-em--next';
      nextPlaceholder.textContent = '+';
      nextPlaceholder.setAttribute('aria-hidden', 'true');
      container.appendChild(nextPlaceholder);
    }

    if (this.giftWrapEnabled) {
      const template = document.getElementById(`GiftWrapSummaryTemplate-${this.dataset.productId}`);
      if (template && template.content) {
        container.appendChild(template.content.cloneNode(true));
      }
    }
  }

  /**
   * Toggle luxury gift wrapping checkbox.
   */
  toggleGiftWrap() {
    this.giftWrapEnabled = !this.giftWrapEnabled;
    const cb = this.querySelector('[data-gift-wrap-cb]');
    if (cb) cb.classList.toggle('checked', this.giftWrapEnabled);
    this.updateSummary();
  }

  /**
   * Open the Perfect Fit Guarantee bottom sheet.
   */
  openPfgPopup() {
    const sheet = this._pfgSheet || this.querySelector('[data-pfg-sheet]');
    const overlay = this._pfgOverlay || this.querySelector('[data-pfg-overlay]');
    if (sheet) sheet.classList.add('open');
    if (overlay) overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  /**
   * Close the Perfect Fit Guarantee bottom sheet.
   */
  closePfgPopup() {
    const sheet = this._pfgSheet || this.querySelector('[data-pfg-sheet]');
    const overlay = this._pfgOverlay || this.querySelector('[data-pfg-overlay]');
    if (sheet) sheet.classList.remove('open');
    if (overlay) overlay.classList.remove('open');
    document.body.style.overflow = '';
  }

  // ─── End New UI Methods ──────────────────────────────────────────────────────

  async refreshCart() {
    const sectionsToBundle = [];

    // Prepare sections
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    // Fetch cart data
    const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();

    // Fetch sections if needed
    let sectionsHtml = {};
    if (sectionsToBundle.length > 0) {
      sectionsHtml = await (await fetch(
        `${Shopify.routes.root}?sections=${sectionsToBundle.join(',')}`
      )).json();
    }

    cartContent.sections = sectionsHtml;

    // Dispatch cart change event
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          onSuccessDo: null,
          cart: cartContent
        }
      })
    );
  }
}

// Register custom element
if (!window.customElements.get('variant-picker-sets')) {
  window.customElements.define('variant-picker-sets', VariantPickerSets);
}
